import { useQuery } from "@tanstack/react-query"
import { type Session, getSession } from "../api/auth"

/**
 * Stable query key for the active session. Exported so mutations
 * (sign-in, sign-out) can invalidate the cache without redefining
 * the key.
 */
export const sessionQueryKey = ["auth", "session"] as const

export interface UseSessionResult {
	data: Session | null | undefined
	isLoading: boolean
	isAuthenticated: boolean
}

/**
 * Read the active engine session.
 *
 * - Cached for 60s (`staleTime`) so multiple consumers on the same
 *   tree don't re-fetch.
 * - `data === undefined` while the first request is in flight.
 * - `data === null` once the server has confirmed there's no
 *   session (204, empty body, or `{}`).
 */
export const useSession = (): UseSessionResult => {
	const { data, isLoading } = useQuery({
		queryKey: sessionQueryKey,
		queryFn: getSession,
		staleTime: 60_000,
	})
	return {
		data,
		isLoading,
		isAuthenticated: Boolean(data?.user),
	}
}
